import { SolarGeometryLocation } from '@/api/mutations/project/useGenerateSolarGeometry';
import useSampleRoofPlane from '@/api/mutations/project/useSampleRoofPlane';
import { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import {
  isPanelWithinRoof,
  createFacePanelFitPredicate,
  makeFrame,
  polygonAreaMeters2,
  ROOF_TEMPLATE_CYCLE,
  type FacePackerPolygon,
  type RoofDrawKind,
  type RoofTemplateResult,
  type SolarLatLng,
} from '@payaca/components/solarRoofView/SolarRoofView';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type SolarDesignDraftState } from '@payaca/helpers/solarDesignDraft';
import { restoreSolarPanelIdentities } from '@payaca/helpers/solarDesignElectricalUpgrade';
import {
  roofEdgeAnchorIsValid,
  type SolarRoofEdgeAnchor,
} from '@payaca/helpers/solarRoofEdgeAnchor';
import { initialiseSolarGeneration } from '@payaca/helpers/solarGenerationAvailability';
import {
  physicalRoofPlane,
  type SolarMounting,
  type SolarRoofPlane,
} from '@payaca/helpers/solarMounting';
import {
  solarAnalysedLayoutStartsFromRepack,
  solarApplyFacePlacement,
  solarEnergyForFaceFrom,
  solarPackAllFaces,
  solarPackFaceWithRules,
  solarPlaceAnalysedFace,
  type SolarFacePackContext,
} from '@payaca/helpers/solarPackLayout';
import {
  hasMeasuredPlane,
  normaliseSolarPlaneFit,
} from '@payaca/helpers/solarPlaneFit';
import { type SolarSavingsRegion } from '@payaca/helpers/solarSavingsConstants';
import {
  DEFAULT_SOLAR_PLACEMENT_RULES,
  type SolarPlacementRules,
} from '@payaca/helpers/solarPlacementRules';
import {
  classifyBoundaryChange,
  dropLinksForFace,
  propagateReshape,
  type FaceVertexLink,
} from './faceLinks';
import {
  computeStraightenDeltaDegrees,
  makePointRotation,
  makeFlushArrayRotation,
  normaliseAzimuthDegrees,
  ringCentroid,
  rotateFacePolygon,
} from './faceRotation';
import { panelCentreOnProperty } from './parcelClip';
import {
  DEFAULT_ADDED_FACE_PITCH_DEGREES,
  DesignerTool,
  EditPanel,
  FIRST_ADDED_FACE_ID,
  WorkingFace,
} from './types';

type UseSolarDesignerLayoutArgs = {
  geometry: ProjectSolarPotential['geometry'];
  centre: ProjectSolarPotential['centre'];
  panelWidthMeters: number | null | undefined;
  panelHeightMeters: number | null | undefined;
  panelCapacityWatts?: number | null;
  projectId: string;
  initialLayout?: SolarDesignDraftState['layout'];
  designId?: string;
  setConfigIndex: (value: number | null) => void;
  generateSolarGeometry: (
    input: {
      projectId: string;
      location?: SolarGeometryLocation;
    },
    options?: { onSettled?: () => void }
  ) => void;
  recordInteraction?: () => void;
  /** Acknowledges only completed packing, including legitimate zero-panel results. */
  onFacesPacked?: (faceIds: number[], rules: SolarPlacementRules) => void;
  placementRules?: SolarPlacementRules;
  region?: SolarSavingsRegion;
  estimatePanelKwh?: (face: WorkingFace) => number | null;
};

const REPOSITION_MOVED_METERS = 1;

const metersBetween = (a: SolarLatLng, b: SolarLatLng): number => {
  const { x, y } = makeFrame(a).toM(b);
  return Math.hypot(x, y);
};

/** 'unmoved': retry with `{ force: true }` to re-check anyway. */
export type ConfirmRepositionOutcome =
  | 'started'
  | 'unmoved'
  | 'no-location'
  | 'rotation-pending';

export const useSolarDesignerLayout = ({
  geometry,
  centre,
  panelWidthMeters,
  panelHeightMeters,
  panelCapacityWatts,
  projectId,
  initialLayout,
  designId,
  setConfigIndex,
  generateSolarGeometry,
  recordInteraction,
  onFacesPacked,
  placementRules = DEFAULT_SOLAR_PLACEMENT_RULES,
  region = 'uk',
  estimatePanelKwh,
}: UseSolarDesignerLayoutArgs) => {
  const packedPanels = geometry?.packedPanels;
  const initialLayoutRef = useRef(initialLayout);
  const panelIdentityNamespace = useRef(designId ?? crypto.randomUUID());
  const initialisedPanelsRef = useRef<{
    signature: string | undefined;
  } | null>(null);
  const [layoutReady, setLayoutReady] = useState(false);
  const generationDimensions = useMemo(
    () => ({
      widthMeters: panelWidthMeters ?? 1.045,
      heightMeters: panelHeightMeters ?? 1.879,
      capacityWatts:
        panelCapacityWatts ?? geometry?.panelDimensions?.capacityWatts ?? 400,
    }),
    [
      panelWidthMeters,
      panelHeightMeters,
      panelCapacityWatts,
      geometry?.panelDimensions?.capacityWatts,
    ]
  );
  const [excludedFaceIds, setExcludedFaceIds] = useState<number[]>([]);
  // Must mirror the server's off-property-excluded rule, or a face edit
  // silently re-fills the neighbour's half of a merged building.
  const parcelRing = geometry?.parcel?.ring ?? null;
  const includedByParcel = useCallback(
    (corners: SolarLatLng[]) => panelCentreOnProperty(corners, parcelRing),
    [parcelRing]
  );

  const [tool, setToolState] = useState<DesignerTool>('select');
  const [drawTemplate, setDrawTemplate] = useState<RoofDrawKind>('outline');
  const cycleDrawTemplate = useCallback(() => {
    setDrawTemplate(
      (current) =>
        ROOF_TEMPLATE_CYCLE[
          (ROOF_TEMPLATE_CYCLE.indexOf(current) + 1) %
            ROOF_TEMPLATE_CYCLE.length
        ]
    );
  }, []);
  /** Welded vertex pairs across template faces: dragging either moves both. */
  const faceLinksRef = useRef<FaceVertexLink[]>([]);

  // ── Edit-first working layout (geometry mode) ─────────────────────────────
  const [workingPanels, setWorkingPanels] = useState<EditPanel[]>([]);
  const [workingFaces, setWorkingFaces] = useState<WorkingFace[]>([]);
  const [selectedFaceId, setSelectedFaceId] = useState<number | null>(null);
  const [selectedObstacleIndex, setSelectedObstacleIndex] = useState<
    number | null
  >(null);
  const [workingObstacles, setWorkingObstacles] = useState<SolarLatLng[][]>([]);
  const [drawPlane, setDrawPlane] = useState<{
    azimuthDegrees: number;
    pitchDegrees: number;
  } | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [repositionPoint, setRepositionPoint] = useState<SolarLatLng | null>(
    null
  );
  /** Row-azimuth offset (deg) applied per face (by id) by the rotate control. */
  const [faceOffsets, setFaceOffsets] = useState<Record<number, number>>({});
  /** The rotation slider value (deg, clockwise), absolute not cumulative. */
  const [selectedFaceRotation, setSelectedFaceRotation] = useState(0);
  /** Absolute slider baseline and its last output; external edits rebase it. */
  const rotationBaseRef = useRef<{
    faceId: number;
    polygon: FacePackerPolygon;
    azimuthDegrees: number;
    degrees: number;
    panels: EditPanel[];
    outputPolygon: FacePackerPolygon;
    outputPanels: EditPanel[];
  } | null>(null);
  const arrayRotationBaseRef = useRef<{
    faceId: number;
    degrees: number;
    panels: EditPanel[];
    outputPanels: EditPanel[];
    polygon: FacePackerPolygon;
    outputDegrees: number;
  } | null>(null);
  const currentLayoutRef = useRef<SolarDesignDraftState['layout'] | null>(null);
  const committedRotationLayoutRef = useRef<
    SolarDesignDraftState['layout'] | null
  >(null);
  const [isArrayRotating, setIsArrayRotating] = useState(false);
  /** Only a pointer that started on the rotation control can finish its drag. */
  const rotationPointerIdRef = useRef<number | null>(null);
  const rotationLayoutDirtyRef = useRef(false);
  const ownerMountedRef = useRef(false);
  useLayoutEffect(() => {
    ownerMountedRef.current = true;
    // Parent layout cleanup precedes the inspector's passive unmount cleanup.
    // Allowed editor navigation discards the preview; it cannot save by queueing
    // another state update on an editor that has already gone away.
    return () => {
      ownerMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    rotationBaseRef.current = null;
    setSelectedFaceRotation(0);
  }, [selectedFaceId]);
  const [layoutDirty, setLayoutDirty] = useState(false);
  /** True once the face/obstacle sets diverge from the server geometry. */
  const [geometryEdited, setGeometryEdited] = useState(false);
  const nextFaceIdRef = useRef(FIRST_ADDED_FACE_ID);
  /** Undo/restore invalidates refinements requested for the previous layout. */
  const layoutEpochRef = useRef(0);
  const [fitKey, setFitKey] = useState(0);

  const restoreLayout = useCallback(
    (layout: SolarDesignDraftState['layout']) => {
      layoutEpochRef.current += 1;
      setWorkingFaces(layout.workingFaces);
      setWorkingPanels(
        restoreSolarPanelIdentities(layout, panelIdentityNamespace.current)
          .workingPanels
      );
      setWorkingObstacles(layout.workingObstacles);
      setFaceOffsets(layout.faceOffsets);
      setExcludedFaceIds(layout.excludedFaceIds);
      setManualMode(layout.manualMode);
      setGeometryEdited(layout.geometryEdited);
      faceLinksRef.current = layout.faceLinks;
      nextFaceIdRef.current = Math.max(
        nextFaceIdRef.current,
        ...layout.workingFaces.map((face) => face.id + 1)
      );
      rotationBaseRef.current = null;
      arrayRotationBaseRef.current = null;
      committedRotationLayoutRef.current = null;
      rotationPointerIdRef.current = null;
      setIsArrayRotating(false);
      setSelectedFaceRotation(0);
      setSelectedFaceId(null);
      setSelectedObstacleIndex(null);
      setToolState('select');
      setLayoutReady(true);
    },
    []
  );

  const addFaceMode = tool === 'draw';
  const addObstacleMode = tool === 'obstacle';
  const moveRoofMode = tool === 'move';
  const footprintPickMode = tool === 'trace';
  const repositionMode = tool === 'pin';

  const setTool = useCallback(
    (next: DesignerTool) => {
      if (committedRotationLayoutRef.current) return;
      setToolState(next);
      if (next === 'draw') setDrawTemplate('outline');
      setDrawPlane(null);
      if (next !== 'select') {
        setSelectedFaceId(null);
        setSelectedObstacleIndex(null);
      }
      setRepositionPoint(
        next === 'pin' && centre ? { lat: centre.lat, lng: centre.lng } : null
      );
    },
    [centre]
  );

  const panelDims = useMemo(
    () => ({
      widthMeters: panelWidthMeters ?? 1.045,
      heightMeters: panelHeightMeters ?? 1.879,
    }),
    [panelWidthMeters, panelHeightMeters]
  );

  /** Indicative per-panel annual energy by face index, from the server's auto layout. */
  const energyForFace = useMemo(
    () => solarEnergyForFaceFrom(packedPanels),
    [packedPanels]
  );

  const buildInitialFaces = useCallback(
    (): WorkingFace[] =>
      (geometry?.faces ?? []).map(
        (face, index): WorkingFace =>
          solarPlaceAnalysedFace(
            {
              id: index,
              polygon: { outer: face.polygon.outer, holes: face.polygon.holes },
              pitchDegrees: face.pitchDegrees,
              planeFit: face.planeFit,
              azimuthDegrees: face.azimuthDegrees,
              roofPlane:
                face.roofPitchDegrees != null && face.roofAzimuthDegrees != null
                  ? {
                      pitchDegrees: face.roofPitchDegrees,
                      azimuthDegrees: face.roofAzimuthDegrees,
                    }
                  : undefined,
              mounting:
                face.roofPitchDegrees != null && face.roofAzimuthDegrees != null
                  ? { mode: 'auto' }
                  : undefined,
              arrayAzimuthDegrees: hasMeasuredPlane(face)
                ? face.azimuthDegrees
                : undefined,
              ...(face.roofPitchDegrees != null &&
              face.roofAzimuthDegrees != null
                ? {
                    appliedMounting: face.panelsOnTiltFrames
                      ? ('tilt' as const)
                      : ('flush' as const),
                  }
                : {}),
              generation: initialiseSolarGeneration(
                face.generation,
                face,
                (packedPanels ?? []).filter(
                  (panel) => panel.faceIndex === index
                ),
                generationDimensions
              ),
              segmentIndex: face.segmentIndex,
              areaMeters2: face.areaMeters2,
              isNew: false,
              pitchSource:
                face.roofPitchDegrees != null && face.roofAzimuthDegrees != null
                  ? ('dsm' as const)
                  : ('assumed' as const),
              groupId: null,
              shading: face.shading ?? null,
              photoAgreement: face.photoAgreement ?? null,
            },
            {
              edited: !!geometry?.edited,
              rules: placementRules,
              generationDimensions,
            }
          )
      ),
    [geometry, packedPanels, generationDimensions, placementRules]
  );

  const buildInitialPanels = useCallback(
    (): EditPanel[] =>
      (packedPanels ?? []).map((panel) => ({
        id: crypto.randomUUID(),
        corners: [
          panel.corners[0],
          panel.corners[1],
          panel.corners[2],
          panel.corners[3],
        ] as EditPanel['corners'],
        orientation:
          panel.orientation === 'LANDSCAPE' ? 'LANDSCAPE' : 'PORTRAIT',
        faceIndex: panel.faceIndex,
        faceId: panel.faceIndex,
        segmentIndex: (panel as { segmentIndex?: number }).segmentIndex ?? -1,
        mcsYearlyEnergyKwh: panel.mcsYearlyEnergyKwh ?? undefined,
        annualAcKwh: panel.annualAcKwh,
        included:
          (panel as { onProperty?: boolean | null }).onProperty !== false,
      })),
    [packedPanels]
  );

  const buildInitialObstacles = useCallback(
    (): SolarLatLng[][] =>
      (geometry?.obstructions ?? []).map((ring) =>
        ring.map((p) => ({ lat: p.lat, lng: p.lng }))
      ),
    [geometry]
  );

  const { mutate: sampleRoofPlane } = useSampleRoofPlane();

  const appliedFace = useCallback(
    (
      face: WorkingFace,
      rules = placementRules,
      offsetDegrees = faceOffsets[face.id] ?? 0
    ): WorkingFace =>
      solarApplyFacePlacement(face, rules, offsetDegrees, generationDimensions),
    [placementRules, faceOffsets, generationDimensions]
  );

  /** The pack inputs Repack uses; `obstacles` and `rules` default to the current ones. */
  const packContext = useCallback(
    (
      overrides: {
        obstacles?: SolarLatLng[][];
        rules?: SolarPlacementRules;
      } = {}
    ): SolarFacePackContext<WorkingFace> => ({
      obstacles: overrides.obstacles ?? workingObstacles,
      panelDims,
      rules: overrides.rules ?? placementRules,
      region,
      energyForFace,
      estimatePanelKwh,
      includedByParcel,
    }),
    [
      workingObstacles,
      panelDims,
      placementRules,
      region,
      energyForFace,
      estimatePanelKwh,
      includedByParcel,
    ]
  );

  const packWorkingFace = useCallback(
    (
      face: WorkingFace,
      polygon: FacePackerPolygon,
      offsetDegrees: number,
      options: {
        obstacles?: SolarLatLng[][];
        rules?: SolarPlacementRules;
        respectRoofSkips?: boolean;
        orientation?: EditPanel['orientation'];
      } = {}
    ): EditPanel[] =>
      solarPackFaceWithRules(
        face,
        offsetDegrees,
        packContext({ obstacles: options.obstacles, rules: options.rules }),
        {
          polygon,
          respectRoofSkips: options.respectRoofSkips,
          orientation: options.orientation,
        }
      ) ?? workingPanels.filter((panel) => panel.faceId === face.id),
    [packContext, workingPanels]
  );

  useEffect(() => {
    // Query refreshes can replace arrays with equivalent data. Preserve local edits.
    const signature = JSON.stringify(packedPanels);
    if (
      initialisedPanelsRef.current &&
      initialisedPanelsRef.current.signature === signature
    )
      return;
    initialisedPanelsRef.current = { signature };
    if (initialLayoutRef.current) {
      const layout = initialLayoutRef.current;
      restoreLayout(layout);
      if (layout.manualMode && layout.workingFaces.length === 0) {
        setToolState('draw');
      }
      initialLayoutRef.current = undefined;
      setFitKey((key) => key + 1);
      return;
    }
    if (!packedPanels) {
      setLayoutReady(true);
      return;
    }
    layoutEpochRef.current += 1;
    // A new analysed layout is exactly what "Repack all roofs" produces from
    // the analysed faces and current rules. The analysis' own panels are only
    // kept for a YourPower layout or where whole-roof Repack would refuse.
    const faces = buildInitialFaces();
    const obstacles = buildInitialObstacles();
    const packed = solarAnalysedLayoutStartsFromRepack(geometry)
      ? solarPackAllFaces({
          faces,
          offsets: {},
          context: { ...packContext({ obstacles }), generationDimensions },
          respectRoofSkips: true,
        })
      : null;
    setWorkingFaces(packed?.faces ?? faces);
    setWorkingObstacles(obstacles);
    setWorkingPanels(packed?.panels ?? buildInitialPanels());
    if (packed)
      onFacesPacked?.(
        faces.map((face) => face.id),
        placementRules
      );
    setFaceOffsets({});
    setExcludedFaceIds([]);
    setSelectedFaceId(null);
    setSelectedObstacleIndex(null);
    setLayoutDirty(false);
    setGeometryEdited(false);
    setConfigIndex(null);
    setToolState('select');
    setDrawPlane(null);
    setRepositionPoint(null);
    setManualMode(false);
    setFitKey((key) => key + 1);
    faceLinksRef.current = [];
    setLayoutReady(true);
  }, [
    packedPanels,
    buildInitialFaces,
    buildInitialObstacles,
    buildInitialPanels,
    setConfigIndex,
    restoreLayout,
    packContext,
    generationDimensions,
    onFacesPacked,
    placementRules,
    geometry,
  ]);

  const repackFaceWithPolygon = useCallback(
    (face: WorkingFace, polygon: FacePackerPolygon, offsetDegrees: number) => {
      // Initial packing must apply the same saved rules as explicit Repack.
      const packed = packWorkingFace(face, polygon, offsetDegrees);
      setWorkingPanels((prev) => [
        ...prev.filter((panel) => panel.faceId !== face.id),
        ...packed,
      ]);
      setWorkingFaces((prev) =>
        prev.map((candidate) =>
          candidate.id === face.id
            ? appliedFace(face, placementRules, offsetDegrees)
            : candidate
        )
      );
      setLayoutDirty(true);
    },
    [packWorkingFace, placementRules, appliedFace]
  );

  const packForFace = useCallback(
    (face: WorkingFace): EditPanel[] =>
      packWorkingFace(face, face.polygon, faceOffsets[face.id] ?? 0),
    [faceOffsets, packWorkingFace]
  );

  const onRotateSelectedFace = useCallback(
    (offsetDegrees: number) => {
      if (selectedFaceId == null || !Number.isFinite(offsetDegrees)) return;
      const face = workingFaces.find(
        (candidate) => candidate.id === selectedFaceId
      );
      if (!face) return;
      if (
        arrayRotationBaseRef.current &&
        arrayRotationBaseRef.current.faceId !== selectedFaceId
      )
        return;
      if (
        face.roofEdgeAnchor ||
        face.appliedRoofEdgeAnchor ||
        face.appliedArrayAzimuthDegrees !== undefined
      ) {
        // Anchored rotation is explicit pending intent relative to the edge.
        setFaceOffsets((prev) => ({
          ...prev,
          [selectedFaceId]: offsetDegrees,
        }));
        setLayoutDirty(true);
        return;
      }
      if (!committedRotationLayoutRef.current) {
        // This transaction owns the layout until Apply/Cancel. Invalidate any
        // older plane sample so it cannot mutate that baseline mid-preview.
        layoutEpochRef.current += 1;
        committedRotationLayoutRef.current = currentLayoutRef.current;
        rotationLayoutDirtyRef.current = layoutDirty;
        setIsArrayRotating(true);
      }
      const panels = workingPanels.filter(
        (panel) => panel.faceId === selectedFaceId
      );
      let base = arrayRotationBaseRef.current;
      if (
        !base ||
        base.faceId !== selectedFaceId ||
        base.polygon !== face.polygon ||
        base.outputPanels.length !== panels.length ||
        panels.some((panel, index) => panel !== base?.outputPanels[index])
      ) {
        base = {
          faceId: selectedFaceId,
          degrees: faceOffsets[selectedFaceId] ?? 0,
          panels,
          outputPanels: panels,
          polygon: face.polygon,
          outputDegrees: offsetDegrees,
        };
        arrayRotationBaseRef.current = base;
      }
      base.outputDegrees = offsetDegrees;
      if (base.panels.length > 0) {
        const pivot = ringCentroid(
          base.panels.flatMap((panel) => panel.corners)
        );
        const rotate =
          face.appliedMounting === 'flush' && face.roofPlane
            ? makeFlushArrayRotation(
                pivot,
                face.roofPlane,
                (face.arrayAzimuthDegrees ?? face.roofPlane.azimuthDegrees) +
                  base.degrees,
                (face.arrayAzimuthDegrees ?? face.roofPlane.azimuthDegrees) +
                  offsetDegrees
              )
            : makePointRotation(pivot, offsetDegrees - base.degrees);
        const rotated = base.panels.map((panel) => ({
          ...panel,
          corners: panel.corners.map(rotate) as EditPanel['corners'],
        }));
        base.outputPanels = rotated;
        let index = 0;
        setWorkingPanels(
          workingPanels.map((panel) =>
            panel.faceId === selectedFaceId ? rotated[index++] : panel
          )
        );
      }
      setFaceOffsets((prev) => ({
        ...prev,
        [selectedFaceId]: offsetDegrees,
      }));
      if (face.appliedMounting === 'tilt' && face.arrayAzimuthDegrees != null) {
        setWorkingFaces((faces) =>
          faces.map((candidate) =>
            candidate.id === selectedFaceId
              ? {
                  ...candidate,
                  azimuthDegrees: normaliseAzimuthDegrees(
                    face.arrayAzimuthDegrees! + offsetDegrees
                  ),
                }
              : candidate
          )
        );
      }
      setLayoutDirty(true);
    },
    [selectedFaceId, workingFaces, workingPanels, faceOffsets, layoutDirty]
  );

  const onCommitSelectedFaceRotation = useCallback(() => {
    if (!ownerMountedRef.current) return;
    rotationPointerIdRef.current = null;
    const base = arrayRotationBaseRef.current;
    if (!committedRotationLayoutRef.current || !base) return;
    const face = workingFaces.find((candidate) => candidate.id === base.faceId);
    if (!face) {
      arrayRotationBaseRef.current = null;
      committedRotationLayoutRef.current = null;
      setIsArrayRotating(false);
      return;
    }
    recordInteraction?.();
    layoutEpochRef.current += 1;
    // Loading a legacy design never invents a missing physical roof plane.
    // Ground geometry still clips it; its existing provenance remains unverified.
    const roof = face.roofPlane ?? {
      pitchDegrees: 0,
      azimuthDegrees: face.azimuthDegrees,
    };
    const mounting = {
      onFrames: face.appliedMounting !== 'flush',
      pitchDegrees: face.pitchDegrees,
    };
    const fits = createFacePanelFitPredicate({
      face: face.polygon,
      pitchDegrees: roof.pitchDegrees,
      azimuthDegrees: roof.azimuthDegrees,
      rowAzimuthDegrees:
        (face.arrayAzimuthDegrees ?? face.azimuthDegrees) + base.outputDegrees,
      mounting,
      obstructions: workingObstacles,
      rules: placementRules,
      region,
    });
    // Filter the actor's current output, not a stale index cache. Queued previews
    // have already been applied before this update, even within one React batch.
    setWorkingPanels((panels) =>
      panels.filter(
        (panel) => panel.faceId !== base.faceId || fits(panel.corners)
      )
    );
    // The parent's exact geometry/plane fingerprint invalidates annual and monthly
    // results. Keep the original provenance so undo restores it without certification.
    arrayRotationBaseRef.current = null;
    committedRotationLayoutRef.current = null;
    setIsArrayRotating(false);
    setLayoutDirty(true);
  }, [
    workingFaces,
    workingObstacles,
    placementRules,
    region,
    recordInteraction,
  ]);

  const onCancelSelectedFaceRotation = useCallback(() => {
    const baseline = committedRotationLayoutRef.current;
    if (!ownerMountedRef.current || !baseline) return;
    layoutEpochRef.current += 1;
    rotationPointerIdRef.current = null;
    arrayRotationBaseRef.current = null;
    committedRotationLayoutRef.current = null;
    setWorkingFaces(baseline.workingFaces);
    setWorkingPanels(baseline.workingPanels);
    setFaceOffsets(baseline.faceOffsets);
    setLayoutDirty(rotationLayoutDirtyRef.current);
    setIsArrayRotating(false);
  }, []);

  const onBeginSelectedFaceRotation = useCallback((pointerId: number) => {
    rotationPointerIdRef.current = pointerId;
  }, []);

  const onEndSelectedFaceRotation = useCallback(
    (pointerId: number) => {
      if (rotationPointerIdRef.current !== pointerId) return;
      rotationPointerIdRef.current = null;
      onCommitSelectedFaceRotation();
    },
    [onCommitSelectedFaceRotation]
  );

  useEffect(() => {
    const finishPointer = (event: PointerEvent) =>
      onEndSelectedFaceRotation(event.pointerId);
    const finishInterruptedDrag = () => {
      if (rotationPointerIdRef.current !== null)
        onEndSelectedFaceRotation(rotationPointerIdRef.current);
    };
    // Listen even before a preview changes: a click without a slider change must
    // clear the gesture too. Numeric previews never arm these completion paths.
    window.addEventListener('pointerup', finishPointer);
    window.addEventListener('pointercancel', finishPointer);
    window.addEventListener('blur', finishInterruptedDrag);
    return () => {
      window.removeEventListener('pointerup', finishPointer);
      window.removeEventListener('pointercancel', finishPointer);
      window.removeEventListener('blur', finishInterruptedDrag);
    };
  }, [onEndSelectedFaceRotation]);

  const onOrientFaceGroup = useCallback(
    (faceId: number, orientation: EditPanel['orientation']) => {
      if (committedRotationLayoutRef.current) return;
      const face = workingFaces.find((candidate) => candidate.id === faceId);
      if (
        !face?.roofPlane ||
        (face.roofEdgeAnchor &&
          (!roofEdgeAnchorIsValid(face.roofEdgeAnchor, face.polygon.outer) ||
            face.roofEdgeAnchor.offsetM < placementRules.edgeSetbackM))
      )
        return;
      const panels = packWorkingFace(
        face,
        face.polygon,
        faceOffsets[faceId] ?? 0,
        { orientation, respectRoofSkips: false }
      );
      setWorkingPanels((previous) => [
        ...previous.filter((panel) => panel.faceId !== faceId),
        ...panels,
      ]);
      setWorkingFaces((faces) =>
        faces.map((candidate) =>
          candidate.id === faceId ? appliedFace(face) : candidate
        )
      );
      setLayoutDirty(true);
    },
    [workingFaces, faceOffsets, packWorkingFace, appliedFace, placementRules]
  );

  /** Rotate a roof and its existing panels together around the roof centroid. */
  const applyFaceRotation = useCallback(
    (
      face: WorkingFace,
      base: {
        polygon: FacePackerPolygon;
        azimuthDegrees: number;
        panels: EditPanel[];
      },
      absoluteDegrees: number
    ) => {
      const polygon = rotateFacePolygon(base.polygon, absoluteDegrees);
      const azimuthDegrees = normaliseAzimuthDegrees(
        base.azimuthDegrees + absoluteDegrees
      );
      const rotate = makePointRotation(
        ringCentroid(base.polygon.outer),
        absoluteDegrees
      );
      const panels = base.panels.map((panel) => ({
        ...panel,
        corners: panel.corners.map(rotate) as EditPanel['corners'],
      }));
      let index = 0;
      setWorkingPanels(
        workingPanels.map((panel) =>
          panel.faceId === face.id ? panels[index++] : panel
        )
      );
      setWorkingFaces((prev) =>
        prev.map((f) =>
          f.id === face.id
            ? {
                ...f,
                polygon,
                azimuthDegrees,
                ...(f.roofPlane
                  ? {
                      roofPlane: {
                        ...f.roofPlane,
                        azimuthDegrees: normaliseAzimuthDegrees(
                          f.roofPlane.azimuthDegrees +
                            azimuthDegrees -
                            f.azimuthDegrees
                        ),
                      },
                    }
                  : {}),
                ...(f.arrayAzimuthDegrees != null
                  ? {
                      arrayAzimuthDegrees: normaliseAzimuthDegrees(
                        f.arrayAzimuthDegrees +
                          azimuthDegrees -
                          f.azimuthDegrees
                      ),
                    }
                  : {}),
                ...(f.appliedArrayAzimuthDegrees != null
                  ? {
                      appliedArrayAzimuthDegrees: normaliseAzimuthDegrees(
                        f.appliedArrayAzimuthDegrees +
                          azimuthDegrees -
                          f.azimuthDegrees
                      ),
                    }
                  : {}),
              }
            : f
        )
      );
      // Rotating a welded face drops its welds rather than dragging partners along.
      faceLinksRef.current = dropLinksForFace(faceLinksRef.current, face.id);
      setGeometryEdited(true);
      setLayoutDirty(true);
      return { polygon, azimuthDegrees, panels };
    },
    [workingPanels]
  );

  const onRotateSelectedFacePolygon = useCallback(
    (absoluteDegrees: number) => {
      if (committedRotationLayoutRef.current) return;
      if (selectedFaceId == null || !Number.isFinite(absoluteDegrees)) return;
      const face = workingFaces.find((f) => f.id === selectedFaceId);
      if (!face) return;
      const panels = workingPanels.filter(
        (panel) => panel.faceId === selectedFaceId
      );
      const base = rotationBaseRef.current;
      // Rebase after any panel edit, repack or other transform. Keep the displayed
      // angle as the baseline so the next slider movement cannot snap backwards.
      if (
        !base ||
        base.faceId !== selectedFaceId ||
        base.outputPolygon !== face.polygon ||
        base.outputPanels.length !== panels.length ||
        panels.some((panel, index) => panel !== base.outputPanels[index])
      ) {
        rotationBaseRef.current = {
          faceId: selectedFaceId,
          polygon: face.polygon,
          azimuthDegrees: face.azimuthDegrees,
          degrees: selectedFaceRotation,
          panels,
          outputPolygon: face.polygon,
          outputPanels: panels,
        };
      }
      const rotationBase = rotationBaseRef.current!;
      const rotated = applyFaceRotation(
        face,
        rotationBase,
        absoluteDegrees - rotationBase.degrees
      );
      rotationBase.outputPolygon = rotated.polygon;
      rotationBase.outputPanels = rotated.panels;
      setSelectedFaceRotation(absoluteDegrees);
    },
    [
      selectedFaceId,
      workingFaces,
      workingPanels,
      selectedFaceRotation,
      applyFaceRotation,
    ]
  );

  /** Whole-roof translation preserves the installer's panel layout and exclusions. */
  const onRoofMoved = useCallback(
    (delta: { dLat: number; dLng: number }) => {
      if (committedRotationLayoutRef.current) return;
      recordInteraction?.();
      const shiftPoint = (p: SolarLatLng): SolarLatLng => ({
        lat: p.lat + delta.dLat,
        lng: p.lng + delta.dLng,
      });
      const shiftedObstacles = workingObstacles.map((ring) =>
        ring.map(shiftPoint)
      );
      const shiftedFaces = workingFaces.map((face) => ({
        ...face,
        polygon: {
          outer: face.polygon.outer.map(shiftPoint),
          holes: face.polygon.holes.map((ring) => ring.map(shiftPoint)),
        },
      }));
      setWorkingObstacles(shiftedObstacles);
      setWorkingFaces(shiftedFaces);
      setGeometryEdited(true);
      rotationBaseRef.current = null;
      setSelectedFaceRotation(0);
      setWorkingPanels((previous) =>
        previous.map((panel) => ({
          ...panel,
          corners: [
            shiftPoint(panel.corners[0]),
            shiftPoint(panel.corners[1]),
            shiftPoint(panel.corners[2]),
            shiftPoint(panel.corners[3]),
          ],
        }))
      );
      setLayoutDirty(true);
    },
    [workingFaces, workingObstacles, recordInteraction]
  );

  // "Straighten": snap the face's longest edge to the building's frame (the largest other face's azimuth).
  const onStraightenSelectedFace = useCallback(() => {
    if (committedRotationLayoutRef.current) return;
    if (selectedFaceId == null) return;
    const face = workingFaces.find((f) => f.id === selectedFaceId);
    if (!face) return;
    const referenceFace = workingFaces
      .filter((f) => f.id !== selectedFaceId)
      .reduce(
        (best, f) =>
          best == null || f.areaMeters2 > best.areaMeters2 ? f : best,
        null as WorkingFace | null
      );
    const delta = computeStraightenDeltaDegrees(
      face.polygon,
      referenceFace?.azimuthDegrees ?? face.azimuthDegrees
    );
    // Already square (within half the slider's resolution) — don't churn.
    if (Math.abs(delta) < 0.5) return;
    const straightened = applyFaceRotation(
      face,
      {
        polygon: face.polygon,
        azimuthDegrees: face.azimuthDegrees,
        panels: workingPanels.filter((panel) => panel.faceId === face.id),
      },
      delta
    );
    rotationBaseRef.current = {
      faceId: selectedFaceId,
      ...straightened,
      degrees: 0,
      outputPolygon: straightened.polygon,
      outputPanels: straightened.panels,
    };
    setSelectedFaceRotation(0);
  }, [selectedFaceId, workingFaces, workingPanels, applyFaceRotation]);

  const onClearSelectedFace = useCallback(() => {
    if (committedRotationLayoutRef.current) return;
    if (selectedFaceId == null) return;
    setWorkingPanels((prev) =>
      prev.filter((panel) => panel.faceId !== selectedFaceId)
    );
    setLayoutDirty(true);
  }, [selectedFaceId]);

  /** An explicit face drag moves its panels too, without moving template partners. */
  const onFaceMoved = useCallback(
    (faceArrayIndex: number, delta: { dLat: number; dLng: number }) => {
      if (committedRotationLayoutRef.current) return;
      const face = workingFaces[faceArrayIndex];
      if (!face || (delta.dLat === 0 && delta.dLng === 0)) return;
      recordInteraction?.();
      const shiftPoint = (point: SolarLatLng): SolarLatLng => ({
        lat: point.lat + delta.dLat,
        lng: point.lng + delta.dLng,
      });
      setWorkingFaces((previous) =>
        previous.map((candidate) =>
          candidate.id === face.id
            ? {
                ...candidate,
                polygon: {
                  outer: candidate.polygon.outer.map(shiftPoint),
                  holes: candidate.polygon.holes.map((ring) =>
                    ring.map(shiftPoint)
                  ),
                },
              }
            : candidate
        )
      );
      setWorkingPanels((previous) =>
        previous.map((panel) =>
          panel.faceId === face.id
            ? {
                ...panel,
                corners: [
                  shiftPoint(panel.corners[0]),
                  shiftPoint(panel.corners[1]),
                  shiftPoint(panel.corners[2]),
                  shiftPoint(panel.corners[3]),
                ],
              }
            : panel
        )
      );
      faceLinksRef.current = dropLinksForFace(faceLinksRef.current, face.id);
      if (rotationBaseRef.current?.faceId === face.id) {
        rotationBaseRef.current = null;
        setSelectedFaceRotation(0);
      }
      setGeometryEdited(true);
      setLayoutDirty(true);
    },
    [workingFaces, recordInteraction]
  );

  const onFaceBoundaryChanged = useCallback(
    (faceArrayIndex: number, polygon: FacePackerPolygon) => {
      if (committedRotationLayoutRef.current) return;
      const face = workingFaces[faceArrayIndex];
      if (!face) return;
      recordInteraction?.();
      const change = classifyBoundaryChange(face.polygon.outer, polygon.outer);

      if (change.kind === 'topology') {
        faceLinksRef.current = dropLinksForFace(faceLinksRef.current, face.id);
      }

      const updates = new Map<number, FacePackerPolygon>();
      if (change.kind === 'reshape' && faceLinksRef.current.length > 0) {
        propagateReshape({
          editedFaceId: face.id,
          moved: change.moved,
          links: faceLinksRef.current,
          polygonsByFaceId: new Map(workingFaces.map((f) => [f.id, f.polygon])),
        }).forEach((partnerPolygon, partnerFaceId) =>
          updates.set(partnerFaceId, partnerPolygon)
        );
      }
      updates.set(face.id, polygon);

      const updatedFaces = workingFaces.map((f) => {
        const next = updates.get(f.id);
        return next
          ? { ...f, polygon: next, areaMeters2: polygonAreaMeters2(next) }
          : f;
      });
      setWorkingFaces(updatedFaces);
      // The map commits once on release. Keep surviving panel objects (including
      // their exact corners and exclusions), and constrain welded partners too.
      setWorkingPanels((previous) =>
        previous.filter((panel) => {
          const updatedPolygon = updates.get(panel.faceId);
          return (
            !updatedPolygon ||
            isPanelWithinRoof(panel.corners, updatedPolygon.outer)
          );
        })
      );
      setGeometryEdited(true);
      if (
        rotationBaseRef.current &&
        updates.has(rotationBaseRef.current.faceId)
      ) {
        rotationBaseRef.current = null;
        setSelectedFaceRotation(0);
      }
      setLayoutDirty(true);
    },
    [workingFaces, recordInteraction]
  );

  const sampleAndApplyPlane = useCallback(
    (face: WorkingFace) => {
      const requestedEpoch = layoutEpochRef.current;
      sampleRoofPlane(
        {
          projectId,
          polygon: {
            outer: face.polygon.outer.map((c) => ({ lat: c.lat, lng: c.lng })),
          },
        },
        {
          onSuccess: (data) => {
            if (requestedEpoch !== layoutEpochRef.current) return;
            const sample = data.sampleRoofPlane;
            if (
              !sample.hasDsm ||
              sample.pitchDegrees == null ||
              sample.azimuthDegrees == null
            ) {
              return;
            }
            const pitchDegrees = sample.pitchDegrees;
            const azimuthDegrees = sample.azimuthDegrees;
            setWorkingFaces((prev) =>
              prev.map((f) =>
                f.id === face.id &&
                f.polygon === face.polygon &&
                f.pitchSource !== 'manual'
                  ? {
                      ...f,
                      roofPlane: { pitchDegrees, azimuthDegrees },
                      pitchSource: 'dsm',
                    }
                  : f
              )
            );
          },
        }
      );
    },
    [projectId, sampleRoofPlane]
  );

  const seedFaceFromPolygon = useCallback(
    (polygon: FacePackerPolygon) => {
      if (committedRotationLayoutRef.current) return;
      const id = nextFaceIdRef.current++;
      const reference =
        (selectedFaceId != null
          ? workingFaces.find((f) => f.id === selectedFaceId)
          : undefined) ?? workingFaces[0];
      // No reference face: default to a southerly azimuth until the DSM refine below replaces it.
      const azimuthDegrees = reference?.roofPlane?.azimuthDegrees ?? 180;
      const pitchDegrees =
        reference?.roofPlane?.pitchDegrees ?? DEFAULT_ADDED_FACE_PITCH_DEGREES;
      const newFace: WorkingFace = appliedFace(
        {
          id,
          polygon,
          pitchDegrees,
          azimuthDegrees,
          roofPlane: { pitchDegrees, azimuthDegrees },
          mounting: { mode: 'auto' },
          segmentIndex: -1,
          areaMeters2: polygonAreaMeters2(polygon),
          isNew: true,
          pitchSource: 'assumed',
          groupId: null,
        },
        placementRules,
        0
      );
      setWorkingFaces((prev) => [...prev, newFace]);
      setGeometryEdited(true);
      repackFaceWithPolygon(newFace, polygon, 0);
      onFacesPacked?.([id], placementRules);
      setSelectedFaceId(id);
      sampleAndApplyPlane(newFace);
    },
    [
      selectedFaceId,
      workingFaces,
      repackFaceWithPolygon,
      sampleAndApplyPlane,
      onFacesPacked,
      appliedFace,
      placementRules,
    ]
  );

  // Fire-and-forget: if the user commits early, the post-commit sampleAndApplyPlane refines the face anyway.
  const onFaceDrawStart = useCallback(
    (corner: SolarLatLng) => {
      if (committedRotationLayoutRef.current) return;
      const requestedEpoch = layoutEpochRef.current;
      setDrawPlane(null);
      const dLat = 1.5 / 111_132;
      const dLng = 1.5 / (111_320 * Math.cos((corner.lat * Math.PI) / 180));
      sampleRoofPlane(
        {
          projectId,
          polygon: {
            outer: [
              { lat: corner.lat - dLat, lng: corner.lng - dLng },
              { lat: corner.lat - dLat, lng: corner.lng + dLng },
              { lat: corner.lat + dLat, lng: corner.lng + dLng },
              { lat: corner.lat + dLat, lng: corner.lng - dLng },
            ],
          },
        },
        {
          onSuccess: (data) => {
            if (requestedEpoch !== layoutEpochRef.current) return;
            const sample = data.sampleRoofPlane;
            if (
              sample.hasDsm &&
              sample.azimuthDegrees != null &&
              sample.pitchDegrees != null
            ) {
              setDrawPlane({
                azimuthDegrees: sample.azimuthDegrees,
                pitchDegrees: sample.pitchDegrees,
              });
            }
          },
        }
      );
    },
    [projectId, sampleRoofPlane]
  );

  const onFaceDrawn = useCallback(
    (polygon: FacePackerPolygon) => {
      if (committedRotationLayoutRef.current) return;
      seedFaceFromPolygon(polygon);
      setToolState('select');
      setDrawPlane(null);
    },
    [seedFaceFromPolygon]
  );

  // Seeds all template faces as one group sharing its welds.
  const onTemplateDrawn = useCallback(
    (result: RoofTemplateResult) => {
      if (committedRotationLayoutRef.current) return;
      const reference = workingFaces[0];
      const pitchDegrees =
        drawPlane?.pitchDegrees ??
        reference?.roofPlane?.pitchDegrees ??
        DEFAULT_ADDED_FACE_PITCH_DEGREES;
      const pitchSource = drawPlane ? ('dsm' as const) : ('assumed' as const);
      const groupId = nextFaceIdRef.current;
      const newFaces: WorkingFace[] = result.faces.map((templateFace) =>
        appliedFace(
          {
            id: nextFaceIdRef.current++,
            polygon: templateFace.polygon,
            pitchDegrees,
            azimuthDegrees: templateFace.azimuthDegrees,
            roofPlane: {
              pitchDegrees,
              azimuthDegrees: templateFace.azimuthDegrees,
            },
            mounting: { mode: 'auto' },
            segmentIndex: -1,
            areaMeters2: polygonAreaMeters2(templateFace.polygon),
            isNew: true,
            pitchSource,
            groupId,
            shading: null,
            photoAgreement: null,
          },
          placementRules,
          0
        )
      );
      setWorkingFaces((prev) => [...prev, ...newFaces]);
      setGeometryEdited(true);
      const packed = newFaces.flatMap(packForFace);
      setWorkingPanels((prev) => [...prev, ...packed]);
      onFacesPacked?.(
        newFaces.map((face) => face.id),
        placementRules
      );
      setLayoutDirty(true);
      faceLinksRef.current = [
        ...faceLinksRef.current,
        ...result.links.map((link) => ({
          a: {
            faceId: newFaces[link.faceA].id,
            vertexIndex: link.vertexA,
          },
          b: {
            faceId: newFaces[link.faceB].id,
            vertexIndex: link.vertexB,
          },
        })),
      ];
      setToolState('select');
      setDrawPlane(null);
      newFaces.forEach((face) => sampleAndApplyPlane(face));
    },
    [
      workingFaces,
      drawPlane,
      packForFace,
      sampleAndApplyPlane,
      onFacesPacked,
      appliedFace,
      placementRules,
    ]
  );

  const planeFitFor = useCallback(
    (face: WorkingFace, roofPlane: SolarRoofPlane) => {
      const analysed = face.isNew ? undefined : geometry?.faces[face.id];
      return analysed?.roofPitchDegrees === roofPlane.pitchDegrees &&
        analysed.roofAzimuthDegrees === roofPlane.azimuthDegrees
        ? normaliseSolarPlaneFit(analysed.planeFit)
        : undefined;
    },
    [geometry]
  );

  /** Sets the face's pitch; marks it manual so later DSM refines don't override it. */
  const onSetSelectedFacePitch = useCallback(
    (pitchDegrees: number) => {
      if (committedRotationLayoutRef.current) return;
      if (selectedFaceId == null || !Number.isFinite(pitchDegrees)) return;
      const face = workingFaces.find((f) => f.id === selectedFaceId);
      if (!face) return;
      if (!face.roofPlane) return;
      const clamped = Math.min(75, Math.max(0, pitchDegrees));
      const targetIds = new Set([
        face.id,
        ...(face.groupId != null
          ? workingFaces
              .filter(
                (f) => f.groupId === face.groupId && f.pitchSource === 'assumed'
              )
              .map((f) => f.id)
          : []),
      ]);
      const updatedFaces = workingFaces.map((f) => {
        if (!targetIds.has(f.id)) return f;
        const roofPlane = {
          pitchDegrees: clamped,
          azimuthDegrees: physicalRoofPlane(f).azimuthDegrees,
        };
        return {
          ...f,
          roofPlane,
          pitchSource: 'manual' as const,
          planeFit: planeFitFor(f, roofPlane),
        };
      });
      setWorkingFaces(updatedFaces);
      setLayoutDirty(true);
    },
    [selectedFaceId, workingFaces, planeFitFor]
  );

  const onConfirmSelectedRoofPlane = useCallback(
    (roofPlane: SolarRoofPlane) => {
      if (committedRotationLayoutRef.current) return;
      if (
        selectedFaceId == null ||
        !Number.isFinite(roofPlane.pitchDegrees) ||
        !Number.isFinite(roofPlane.azimuthDegrees) ||
        roofPlane.pitchDegrees < 0 ||
        roofPlane.pitchDegrees > 75 ||
        roofPlane.azimuthDegrees < 0 ||
        roofPlane.azimuthDegrees > 360
      )
        return;
      setWorkingFaces((faces) =>
        faces.map((face) =>
          face.id === selectedFaceId
            ? {
                ...face,
                roofPlane,
                pitchSource: 'manual',
                planeFit: planeFitFor(face, roofPlane),
                arrayAzimuthDegrees: hasMeasuredPlane(face)
                  ? (face.arrayAzimuthDegrees ?? face.azimuthDegrees)
                  : roofPlane.azimuthDegrees,
              }
            : face
        )
      );
      setLayoutDirty(true);
    },
    [selectedFaceId, planeFitFor]
  );

  const onSetSelectedFaceMounting = useCallback(
    (mounting: SolarMounting) => {
      if (committedRotationLayoutRef.current) return;
      if (selectedFaceId == null) return;
      setWorkingFaces((faces) =>
        faces.map((face) =>
          face.id === selectedFaceId && face.roofPlane
            ? { ...face, mounting }
            : face
        )
      );
      setLayoutDirty(true);
    },
    [selectedFaceId]
  );

  const onFootprintPick = useCallback(
    (ring: SolarLatLng[]) => {
      if (committedRotationLayoutRef.current) return;
      seedFaceFromPolygon({ outer: ring, holes: [] });
      setToolState('select');
    },
    [seedFaceFromPolygon]
  );

  const onDeleteSelectedFace = useCallback(() => {
    if (committedRotationLayoutRef.current) return;
    if (selectedFaceId == null) return;
    setWorkingFaces((prev) => prev.filter((f) => f.id !== selectedFaceId));
    setWorkingPanels((prev) =>
      prev.filter((panel) => panel.faceId !== selectedFaceId)
    );
    setExcludedFaceIds((prev) => prev.filter((id) => id !== selectedFaceId));
    setFaceOffsets((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([id]) => Number(id) !== selectedFaceId)
      )
    );
    faceLinksRef.current = dropLinksForFace(
      faceLinksRef.current,
      selectedFaceId
    );
    setSelectedFaceId(null);
    setGeometryEdited(true);
    setLayoutDirty(true);
  }, [selectedFaceId]);

  const onToggleSelectedFaceIncluded = useCallback(() => {
    if (committedRotationLayoutRef.current) return;
    if (selectedFaceId == null) return;
    const include = excludedFaceIds.includes(selectedFaceId);
    setExcludedFaceIds((previous) =>
      previous.includes(selectedFaceId)
        ? previous.filter((id) => id !== selectedFaceId)
        : [...previous, selectedFaceId]
    );
    setWorkingPanels((previous) =>
      previous.map((panel) =>
        panel.faceId === selectedFaceId
          ? { ...panel, included: include }
          : panel
      )
    );
    setLayoutDirty(true);
  }, [excludedFaceIds, selectedFaceId]);

  // Replaces the whole working panel set — per-panel toggles are not preserved.
  const replotAllFaces = useCallback(
    (
      offsets: Record<number, number> = faceOffsets,
      packOpts?: { rules?: SolarPlacementRules; respectRoofSkips?: boolean }
    ) => {
      const packed = solarPackAllFaces({
        faces: workingFaces,
        offsets,
        context: {
          ...packContext({ rules: packOpts?.rules }),
          generationDimensions,
        },
        respectRoofSkips: packOpts?.respectRoofSkips ?? false,
      });
      if (!packed) return [];
      setWorkingPanels(packed.panels);
      setWorkingFaces(packed.faces);
      const packedIds = workingFaces.map((face) => face.id);
      if (packOpts?.respectRoofSkips)
        onFacesPacked?.(packedIds, packOpts.rules ?? placementRules);
      return packedIds;
    },
    [
      workingFaces,
      faceOffsets,
      packContext,
      generationDimensions,
      onFacesPacked,
      placementRules,
    ]
  );

  // ── Obstacle editing ──────────────────────────────────────────────────────

  // The tool stays armed after each draw, until Esc or the rail disarms it.
  const onObstacleDrawn = useCallback(
    (ring: SolarLatLng[]) => {
      if (committedRotationLayoutRef.current) return;
      const next = [...workingObstacles, ring];
      setWorkingObstacles(next);
      setGeometryEdited(true);
      setLayoutDirty(true);
    },
    [workingObstacles]
  );

  const onObstacleRemove = useCallback(
    (index: number) => {
      if (committedRotationLayoutRef.current) return;
      const removed = workingObstacles[index];
      if (!removed) return;
      const next = workingObstacles.filter((_, i) => i !== index);
      setWorkingObstacles(next);
      setSelectedObstacleIndex(null);
      setGeometryEdited(true);
      setLayoutDirty(true);
    },
    [workingObstacles]
  );

  const onResetToAuto = useCallback(() => {
    if (committedRotationLayoutRef.current) return;
    if (!packedPanels && !manualMode) return;
    setFaceOffsets({});
    setSelectedFaceId(null);
    setSelectedObstacleIndex(null);
    replotAllFaces({}, { respectRoofSkips: true });
    setLayoutDirty(true);
  }, [packedPanels, manualMode, replotAllFaces]);

  const repackAllFaces = useCallback(
    (packOpts?: { rules?: SolarPlacementRules }) => {
      if (committedRotationLayoutRef.current) return [];
      const packedIds = replotAllFaces(faceOffsets, {
        rules: packOpts?.rules,
        respectRoofSkips: true,
      });
      if (packedIds.length > 0) setLayoutDirty(true);
      return packedIds;
    },
    [replotAllFaces, faceOffsets]
  );

  /**
   * An Array rules change: repacks every roof under `rules` but keeps the
   * panels (and their identities, so string membership survives) of each
   * roof whose packing the change does not affect. Returns the ids of the
   * roofs whose panels changed, or null when whole-roof Repack refuses.
   */
  const repackAffectedFaces = useCallback(
    (rules: SolarPlacementRules): number[] | null => {
      if (committedRotationLayoutRef.current) return null;
      const packed = solarPackAllFaces({
        faces: workingFaces,
        offsets: faceOffsets,
        context: { ...packContext({ rules }), generationDimensions },
        respectRoofSkips: true,
      });
      if (!packed) return null;
      const shape = (panels: EditPanel[]) =>
        JSON.stringify(
          panels
            .map((panel) => [panel.orientation, panel.corners, panel.included])
            .map((entry) => JSON.stringify(entry))
            .sort()
        );
      const changed: number[] = [];
      const faceIds = new Set(workingFaces.map((face) => face.id));
      const panels = [
        ...workingPanels.filter((panel) => !faceIds.has(panel.faceId)),
        ...workingFaces.flatMap((face) => {
          const before = workingPanels.filter(
            (panel) => panel.faceId === face.id
          );
          const after = packed.panels.filter(
            (panel) => panel.faceId === face.id
          );
          if (shape(before) === shape(after)) return before;
          changed.push(face.id);
          return after;
        }),
      ];
      setWorkingPanels(panels);
      setWorkingFaces(packed.faces);
      onFacesPacked?.(
        workingFaces.map((face) => face.id),
        rules
      );
      setLayoutDirty(true);
      return changed;
    },
    [
      workingFaces,
      workingPanels,
      faceOffsets,
      packContext,
      generationDimensions,
      onFacesPacked,
    ]
  );

  const repackFace = useCallback(
    (faceId: number, rules: SolarPlacementRules) => {
      if (committedRotationLayoutRef.current) return [];
      const face = workingFaces.find((candidate) => candidate.id === faceId);
      if (!face || !face.roofPlane) return [];
      if (
        face.roofEdgeAnchor &&
        (!roofEdgeAnchorIsValid(face.roofEdgeAnchor, face.polygon.outer) ||
          face.roofEdgeAnchor.offsetM < rules.edgeSetbackM)
      )
        return [];
      const panels = packWorkingFace(
        face,
        face.polygon,
        faceOffsets[faceId] ?? 0,
        {
          rules,
          respectRoofSkips: true,
        }
      );
      setWorkingPanels((previous) => [
        ...previous.filter((panel) => panel.faceId !== faceId),
        ...panels,
      ]);
      setWorkingFaces((faces) =>
        faces.map((candidate) =>
          candidate.id === faceId ? appliedFace(face, rules) : candidate
        )
      );
      setLayoutDirty(true);
      onFacesPacked?.([faceId], rules);
      return [faceId];
    },
    [workingFaces, faceOffsets, packWorkingFace, appliedFace, onFacesPacked]
  );

  /**
   * "Include anyway" (enabled) or "Follow rules": records the roof's rules
   * override, which every repack respects, and repacks that roof.
   */
  const setFaceRulesOverride = useCallback(
    (faceId: number, enabled: boolean, rules: SolarPlacementRules) => {
      if (committedRotationLayoutRef.current) return [];
      const face = workingFaces.find((candidate) => candidate.id === faceId);
      if (!face?.roofPlane) return [];
      const next: WorkingFace = { ...face };
      if (enabled) next.rulesOverride = true;
      else delete next.rulesOverride;
      const panels = packWorkingFace(
        next,
        next.polygon,
        faceOffsets[faceId] ?? 0,
        { rules, respectRoofSkips: true }
      );
      setWorkingPanels((previous) => [
        ...previous.filter((panel) => panel.faceId !== faceId),
        ...panels,
      ]);
      setWorkingFaces((faces) =>
        faces.map((candidate) =>
          candidate.id === faceId ? appliedFace(next, rules) : candidate
        )
      );
      setLayoutDirty(true);
      onFacesPacked?.([faceId], rules);
      return [faceId];
    },
    [workingFaces, faceOffsets, packWorkingFace, appliedFace, onFacesPacked]
  );

  const startManualDraw = useCallback(() => {
    if (committedRotationLayoutRef.current) return;
    layoutEpochRef.current += 1;
    setManualMode(true);
    setWorkingFaces([]);
    setWorkingObstacles([]);
    setWorkingPanels([]);
    setFaceOffsets({});
    setExcludedFaceIds([]);
    setSelectedFaceId(null);
    setSelectedObstacleIndex(null);
    setLayoutDirty(false);
    setConfigIndex(null);
    setToolState('draw');
    setRepositionPoint(null);
    faceLinksRef.current = [];
  }, [setConfigIndex]);

  const onSetSelectedFaceAnchor = useCallback(
    (anchor: SolarRoofEdgeAnchor | null) => {
      if (committedRotationLayoutRef.current) return;
      if (selectedFaceId == null) return;
      const previous = workingFaces.find(
        (face) => face.id === selectedFaceId
      )?.roofEdgeAnchor;
      setWorkingFaces((faces) =>
        faces.map((face) => {
          if (face.id !== selectedFaceId) return face;
          // Capture before resetting the reference-relative angle. Tilt's
          // panel bearing is applied metadata, unlike pending faceOffsets.
          // Flush legacy offsets rotate panels immediately, but only a
          // recorded baseline and physical plane establish those axes.
          let appliedArrayAzimuthDegrees =
            face.appliedArrayAzimuthDegrees ?? null;
          if (
            face.appliedArrayAzimuthDegrees === undefined &&
            !face.roofEdgeAnchor &&
            !face.appliedRoofEdgeAnchor
          ) {
            if (face.appliedMounting === 'tilt')
              appliedArrayAzimuthDegrees = normaliseAzimuthDegrees(
                face.azimuthDegrees
              );
            else if (
              face.appliedMounting === 'flush' &&
              face.roofPlane &&
              face.arrayAzimuthDegrees != null
            )
              appliedArrayAzimuthDegrees = normaliseAzimuthDegrees(
                face.arrayAzimuthDegrees + (faceOffsets[face.id] ?? 0)
              );
          }
          return {
            ...face,
            roofEdgeAnchor: anchor,
            appliedArrayAzimuthDegrees,
          };
        })
      );
      if (
        previous?.edgeIndex !== anchor?.edgeIndex ||
        JSON.stringify(previous?.endpoints) !==
          JSON.stringify(anchor?.endpoints)
      )
        setFaceOffsets((offsets) => ({ ...offsets, [selectedFaceId]: 0 }));
      setLayoutDirty(true);
    },
    [selectedFaceId, workingFaces, faceOffsets]
  );

  // ── Pin tool (correct the analysis location) ──────────────────────────────
  // Confirm also persists the corrected lat/long on the site address.
  /** True once the pin has moved from the analysed point — confirming is billable. */
  const repositionMoved = useMemo(() => {
    if (!repositionPoint) return false;
    if (!centre) return true;
    return metersBetween(centre, repositionPoint) >= REPOSITION_MOVED_METERS;
  }, [repositionPoint, centre]);

  /** Stays armed until the mutation settles, so a duplicate click can't spend another billable request. */
  const confirmReposition = useCallback(
    (options?: { force?: boolean }): ConfirmRepositionOutcome => {
      if (committedRotationLayoutRef.current) return 'rotation-pending';
      const location =
        repositionPoint ??
        (centre ? { lat: centre.lat, lng: centre.lng } : undefined);
      if (!location) return 'no-location';
      if (!repositionMoved && !options?.force) return 'unmoved';
      generateSolarGeometry(
        { projectId, location },
        {
          onSettled: () => {
            setToolState('select');
            setRepositionPoint(null);
          },
        }
      );
      return 'started';
    },
    [repositionPoint, repositionMoved, centre, generateSolarGeometry, projectId]
  );

  const geometryMode = !!packedPanels || manualMode;

  // Panel tools and count controls also use this public setter. They share the
  // same owner boundary as roof/map handlers, including calls in one React batch.
  const setEditableWorkingPanels = useCallback(
    (update: Parameters<typeof setWorkingPanels>[0]) => {
      if (committedRotationLayoutRef.current) return;
      setWorkingPanels(update);
    },
    []
  );

  const visibleWorkingPanels = useMemo(
    () =>
      workingPanels.map((panel) =>
        excludedFaceIds.includes(panel.faceId)
          ? { ...panel, included: false }
          : panel
      ),
    [workingPanels, excludedFaceIds]
  );

  const layoutSnapshot: SolarDesignDraftState['layout'] = {
    workingFaces,
    workingPanels,
    workingObstacles,
    faceOffsets,
    faceLinks: faceLinksRef.current,
    excludedFaceIds,
    manualMode,
    geometryEdited,
  };
  currentLayoutRef.current = layoutSnapshot;
  const committedLayoutSnapshot =
    isArrayRotating && committedRotationLayoutRef.current
      ? committedRotationLayoutRef.current
      : layoutSnapshot;

  return {
    onSetSelectedFaceAnchor,
    // state
    tool,
    setTool,
    drawTemplate,
    setDrawTemplate,
    cycleDrawTemplate,
    workingPanels: visibleWorkingPanels,
    workingFaces,
    workingObstacles,
    faceOffsets,
    selectedFaceRotation,
    selectedFaceId,
    selectedObstacleIndex,
    manualMode,
    repositionMode,
    repositionPoint,
    repositionMoved,
    layoutDirty,
    geometryMode,
    fitKey,
    layoutReady,
    layoutSnapshot,
    committedLayoutSnapshot,
    isArrayRotating,
    onCommitSelectedFaceRotation,
    onBeginSelectedFaceRotation,
    onEndSelectedFaceRotation,
    onCancelSelectedFaceRotation,
    restoreLayout,
    excludedFaceIds,
    onToggleSelectedFaceIncluded,
    // setters used directly by the UI / cross-cutting handlers
    setSelectedFaceId,
    setSelectedObstacleIndex,
    setWorkingPanels: setEditableWorkingPanels,
    setLayoutDirty,
    setRepositionPoint,
    // derived mode flags (all views of `tool`)
    addFaceMode,
    addObstacleMode,
    moveRoofMode,
    footprintPickMode,
    // handlers
    onRotateSelectedFace,
    onRotateSelectedFacePolygon,
    onStraightenSelectedFace,
    onRoofMoved,
    onFaceMoved,
    drawPlane,
    onFaceDrawStart,
    onOrientFaceGroup,
    onClearSelectedFace,
    onFaceBoundaryChanged,
    onFaceDrawn,
    onTemplateDrawn,
    onSetSelectedFacePitch,
    onSetSelectedFaceMounting,
    onConfirmSelectedRoofPlane,
    onFootprintPick,
    onDeleteSelectedFace,
    onObstacleDrawn,
    onObstacleRemove,
    onResetToAuto,
    repackAllFaces,
    repackAffectedFaces,
    repackFace,
    setFaceRulesOverride,
    startManualDraw,
    confirmReposition,
  };
};
